import { Box, Typography } from "@bca/ui-library";
import { InsuranceTotalLoss } from "../vehicleDetails/vehicle-details.type";

import CarAlertSVG from "./Icons/CarAlertSVG";
import ChangeOfRegistrationSVG from "./Icons/ChangeOfRegistrationSVG";
import MajorDamageSVG from "./Icons/MajorDamageSVG";
import ModifiedVehicleSVG from "./Icons/ModifiedVehicleSVG";

const formatDate = (isoDateString: string) => {
  if (!isoDateString || isoDateString.length === 0) {
    return "";
  }

  const date = new Date(isoDateString).toLocaleDateString("en-GB", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
  });

  return date.toString();
};

interface DeclarationItemProps {
  value: string;
  children: React.ReactElement;
}

export const DeclarationItem = ({ value, children }: DeclarationItemProps) => {
  return (
    <Box
      marginBottom={{ mobile: 9, tablet: "44px", desktop: "24px" }}
      width={{ mobile: "175px", tablet: "200px", desktop: "200px" }}
    >
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        flex={1}
        minWidth={0}
        height={19}
        maxWidth="calc(100% - 20px)"
        marginBottom={[7, 7, 9]}
      >
        {children}
        <Typography
          variant="body"
          color="grey-blue"
          display="inline-flex"
          fontWeight="medium"
          css={`
            margin: 0;
            margin-left: 8px;
            line-height: 1.36;
            text-transform: capitalize;
            align-self: flex-start;
          `}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

interface DeclarationProps {
  majorDamage?: boolean;
  ownedByPolice?: boolean;
  usedAsTaxi?: boolean;
  reregistered?: boolean;
  changeOfRegistration?: boolean;
  modifiedVehicle?: boolean;
  insuranceTotalLoss?: InsuranceTotalLoss[];
}

export const DeclarationSection = ({
  changeOfRegistration,
  majorDamage,
  ownedByPolice,
  usedAsTaxi,
  reregistered,
  modifiedVehicle,
  insuranceTotalLoss,
}: DeclarationProps) => {
  const showDeclarationSection =
    changeOfRegistration ||
    majorDamage ||
    ownedByPolice ||
    usedAsTaxi ||
    reregistered ||
    modifiedVehicle ||
    (insuranceTotalLoss && insuranceTotalLoss?.length > 0);
  if (showDeclarationSection)
    return (
      <Box
        display="flex"
        flexWrap={["wrap", "wrap", "nowrap", "nowrap"]}
        flexDirection={["column", "row"]}
        paddingY={0}
      >
        <Box marginBottom={{ mobile: "8px", tablet: "8px", desktop: "24px" }}>
          <Typography variant="h5" css="margin: 0 0 24px 0;">
            Declarations
          </Typography>
          <Box display="flex" flexWrap="wrap" justifyContent="flex-start">
            {changeOfRegistration && (
              <DeclarationItem value="Change of Registration">
                <ChangeOfRegistrationSVG marginX={0} mt={1} />
              </DeclarationItem>
            )}
            {reregistered && (
              <DeclarationItem value="Reregistered or Imported">
                <ChangeOfRegistrationSVG marginX={0} mt={1} />
              </DeclarationItem>
            )}
            {insuranceTotalLoss?.map((item) => (
              <DeclarationItem
                key={`total-loss-${item.lossCode}-${item.lossDate}`}
                value={`Total Loss Cat ${item.lossCode} ${formatDate(
                  item.lossDate
                )}`}
              >
                <MajorDamageSVG mt={1} marginX={0} />
              </DeclarationItem>
            ))}
            {majorDamage && (
              <DeclarationItem value="Serious Accident Damage">
                <MajorDamageSVG mt={1} marginX={0} />
              </DeclarationItem>
            )}
            {ownedByPolice && (
              <DeclarationItem value="Owned by Police">
                <CarAlertSVG mt={1} marginX={0} />
              </DeclarationItem>
            )}
            {usedAsTaxi && (
              <DeclarationItem value="Taxi / Private Hire">
                <CarAlertSVG mt={1} marginX={0} />
              </DeclarationItem>
            )}
            {modifiedVehicle && (
              <DeclarationItem value="Modified Vehicle">
                <ModifiedVehicleSVG mt={1} marginX={0} />
              </DeclarationItem>
            )}
          </Box>
        </Box>
      </Box>
    );
  return (
    <Box marginBottom="44px" marginTop={{ mobile: 0, tablet: 0, desktop: 0 }}>
      <Typography variant="h5" css="margin: 0 0 24px 0;">
        Declarations
      </Typography>
      <Typography
        variant="body"
        fontStyle="italic"
        color="grey-blue"
        fontWeight="normal"
      >
        No declarations made
      </Typography>
    </Box>
  );
};
